/* eslint-disable max-lines */
import {
  usePresenceRules,
  usePresenceZones,
  useUpdatePresenceRules,
} from '@hakimo-ui/hakimo/data-access';
import {
  CameraDetail,
  PresenceLiveness,
  PresenceRule,
} from '@hakimo-ui/hakimo/types';
import { toast, useCanUpdatePresenceRules } from '@hakimo-ui/hakimo/util';
import { Alert, Button, Toggle } from '@hakimo-ui/shared/ui-base';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCallback, useMemo, useState } from 'react';
import { AddEditPresenceRule } from './AddEditPresenceRule';
import { describeRule, zoneLabel } from './ruleSentence';

interface Props {
  camera: CameraDetail;
  liveness?: PresenceLiveness;
}

type Editing = { kind: 'add' } | { kind: 'edit'; rule: PresenceRule } | null;

/**
 * Two rules are the same rule when they name the same zone with the same
 * type. There is no id to go by, and addressing by list position breaks when
 * the list refetches while the modal is open.
 *
 * ponytail: two identical rules on one zone are indistinguishable here and
 * toggle together. That config is degenerate; give rules ids to separate it.
 */
function sameRule(a: PresenceRule, b: PresenceRule): boolean {
  return (
    a.type === b.type && a.zoneId === b.zoneId && a.zoneName === b.zoneName
  );
}

/**
 * The rules this camera runs.
 *
 * Rules this camera owns are editable here and saved as one list. Tenant-wide
 * rules -- addressed by zone name with no camera -- run on every camera; they
 * are shown so nobody wonders why an alert fired, but edited in the tenant
 * configuration.
 */
export function PresenceRules({ camera, liveness }: Props) {
  const canUpdate = useCanUpdatePresenceRules();
  const { data, isLoading, isError, error } = usePresenceRules(camera.id);
  const { data: zones } = usePresenceZones(camera.id);
  const [editing, setEditing] = useState<Editing>(null);
  const [pending, setPending] = useState<PresenceRule | null>(null);
  const [saveProblem, setSaveProblem] = useState<string | null>(null);

  const updateMutation = useUpdatePresenceRules(camera.id, () => {
    toast('Presence rules saved', { type: 'success' });
    setEditing(null);
    setPending(null);
  });

  const zoneIds = useMemo(
    () => new Set((zones ?? []).map((z) => z.id)),
    [zones]
  );

  /**
   * A rule this camera owns is one the save replaces. The backend replaces
   * both the rules scoped to this camera and any rule addressing one of its
   * zones by id, whatever its scope -- so a rule left out of the list on the
   * grounds that it carries no cameraId is deleted by the next save.
   */
  const isOwn = useCallback(
    (rule: PresenceRule) =>
      rule.cameraId === camera.id ||
      (!!rule.zoneId && zoneIds.has(rule.zoneId)),
    [camera.id, zoneIds]
  );

  const scoped = useMemo(
    () => (data?.rules ?? []).filter(isOwn),
    [data, isOwn]
  );
  const inherited = useMemo(
    () => (data?.rules ?? []).filter((r) => !isOwn(r)),
    [data, isOwn]
  );

  /**
   * The camera save refuses a rule that has no zoneId, and a rule written
   * before ids comes back from the server with only a zone name. Resolve the
   * name to this camera's zone before sending, so one legacy rule does not
   * make every save on the camera fail.
   */
  const save = useCallback(
    (rules: PresenceRule[]) => {
      // The zones decide both what this camera owns and how a name resolves.
      if (!zones) return;
      const resolved = rules.map((rule) =>
        rule.zoneId
          ? rule
          : {
              ...rule,
              zoneId: zones.find((z) => z.name === rule.zoneName)?.id ?? null,
            }
      );
      const orphan = resolved.find((rule) => !rule.zoneId);
      if (orphan) {
        setPending(null);
        setSaveProblem(
          `The rule on "${zoneLabel(
            orphan.zoneName
          )}" points at a zone this camera no longer has, so it cannot be saved. Delete that rule to save the rest.`
        );
        return;
      }
      setSaveProblem(null);
      updateMutation.mutate({ cameraId: camera.id, rules: resolved });
    },
    [updateMutation, camera.id, zones]
  );

  const handleSaveRule = useCallback(
    (rule: PresenceRule) => {
      if (!editing) return;
      save(
        editing.kind === 'add'
          ? [...scoped, rule]
          : scoped.map((r) => (sameRule(r, editing.rule) ? rule : r))
      );
    },
    [editing, scoped, save]
  );

  const handleToggle = useCallback(
    (rule: PresenceRule, enabled: boolean) => {
      setPending(rule);
      save(scoped.map((r) => (sameRule(r, rule) ? { ...r, enabled } : r)));
    },
    [scoped, save]
  );

  const handleDelete = useCallback(
    (rule: PresenceRule) => {
      setPending(rule);
      save(scoped.filter((r) => !sameRule(r, rule)));
    },
    [scoped, save]
  );

  const isSaving = updateMutation.isLoading;
  const editingRule = editing?.kind === 'edit' ? editing.rule : undefined;
  const warnings = data?.warnings ?? [];

  return (
    <div className="space-y-3">
      {isError && (
        <Alert type="error">{error?.message ?? 'Could not load rules'}</Alert>
      )}
      {data && !data.effectiveEnabled && (
        <Alert type="warning">
          Presence is switched off for this camera. Rules are saved but nothing
          is evaluated until it is switched on.
        </Alert>
      )}
      {data && data.effectiveEnabled && !data.emitAlarms && (
        <Alert type="info">
          Rules run in shadow mode: they are evaluated and recorded, but no
          alerts reach operators yet.
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert type="warning">
          <ul className="list-disc space-y-1 pl-4">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}
      {saveProblem && <Alert type="error">{saveProblem}</Alert>}
      {updateMutation.isError && !editing && (
        <Alert type="error">{updateMutation.error?.message}</Alert>
      )}

      <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 rounded-lg">
        {isLoading && (
          <p className="text-onlight-text-3 dark:text-ondark-text-3 p-4 text-sm">
            Loading rules…
          </p>
        )}
        {!isLoading && scoped.length === 0 && (
          <p className="text-onlight-text-3 dark:text-ondark-text-3 p-4 text-sm">
            No rules on this camera yet.
          </p>
        )}
        <ul className="divide-onlight-line-2 dark:divide-ondark-line-2 divide-y">
          {scoped.map((rule, index) => (
            <li
              key={`${rule.type}-${rule.zoneId ?? rule.zoneName}-${index}`}
              className="flex items-center gap-3 px-4 py-3"
            >
              <Toggle
                enabled={rule.enabled}
                onChange={
                  canUpdate && !isSaving
                    ? (v) => handleToggle(rule, v)
                    : undefined
                }
                title={rule.enabled ? 'Enabled' : 'Disabled'}
              />
              <div className="min-w-0 flex-1">
                <p className="text-onlight-text-1 dark:text-ondark-text-1 truncate text-sm">
                  {describeRule(rule)}
                </p>
                <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
                  {zoneLabel(rule.zoneName)}
                </p>
              </div>
              {canUpdate && (
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="icon"
                    title="Edit rule"
                    onClick={() => setEditing({ kind: 'edit', rule })}
                    disabled={isSaving}
                  >
                    <PencilIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-5 w-5" />
                  </Button>
                  <Button
                    variant="icon"
                    title="Delete rule"
                    onClick={() => handleDelete(rule)}
                    disabled={isSaving}
                    loading={isSaving && !!pending && sameRule(pending, rule)}
                  >
                    <TrashIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-5 w-5" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
        {inherited.length > 0 && (
          <div className="border-onlight-line-2 dark:border-ondark-line-2 border-t px-4 py-3">
            <p className="text-onlight-text-3 dark:text-ondark-text-3 mb-1 text-xs uppercase tracking-wide">
              Tenant-wide · runs on every camera
            </p>
            <p className="text-onlight-text-3 dark:text-ondark-text-3 mb-2 text-xs">
              Set in the tenant configuration rather than on this camera.
            </p>
            <ul className="space-y-1">
              {inherited.map((rule, index) => (
                <li
                  key={`inherited-${index}`}
                  className="text-onlight-text-2 dark:text-ondark-text-2 text-sm"
                >
                  {describeRule(rule)}
                  {!rule.enabled && ' (disabled)'}
                </li>
              ))}
            </ul>
          </div>
        )}
        {canUpdate && (
          <div className="flex justify-end px-4 py-3">
            <Button
              variant="primary"
              onClick={() => setEditing({ kind: 'add' })}
              disabled={!zones || zones.length === 0 || isSaving}
            >
              <PlusIcon className="mr-1 h-4 w-4" />
              Add rule
            </Button>
          </div>
        )}
      </div>

      {editing && zones && (
        <AddEditPresenceRule
          cameraId={camera.id}
          zones={zones}
          rule={editingRule}
          liveness={liveness}
          isSaving={isSaving}
          error={updateMutation.isError ? updateMutation.error?.message : null}
          onSave={handleSaveRule}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

export default PresenceRules;
